import {
  Button,
  Column,
  Container,
  Copy,
  Flex,
  Heading,
  Image,
  Loader,
  Row,
  Section,
  Square,
  Stack,
  UtilityBanner,
} from '@grubhubprod/gh-cookbook-react/dist';
import {
  IEventData,
  publish,
} from '@grubhubprod/umami-library/dist/utilities/eventManager';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  getGroupIdsFromLocList,
  getLocHasDiners,
} from 'src/components/selfService/features/LineOfCredit/LineOfCreditLanding.utils';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import LineOfCreditCard from 'src/components/selfService/features/LineOfCredit/LineOfCreditCard';
import { LineOfCreditModal } from 'src/components/selfService/features/LineOfCredit/LineOfCreditModal';
import { SnackbarStatus } from 'src/components/snackbar/SnackbarLauncher';
import {
  getFormattedLocBudgetAmount,
  getDayLocFrequency,
  getWeekLocDates,
} from 'src/models/LineOfCreditModelUtils';
import {
  ILineOfCreditPayload,
  ILineOfCreditResponseType,
  LineOfCreditAsyncTasks,
} from 'src/types/LineOfCredit';
import { sdk } from 'src/utilities/sdk';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import {
  LineOfCreditSearchType,
  SearchStatus,
  useLineOfCreditSearch,
} from 'src/utilities/useSearchHooks';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { AnalyticsEventLabel } from 'src/types/Analytics';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { useBalanceAccountInfo } from 'src/components/selfService/common/Balance/hooks/useBalanceAccountInfo';
import { useFetchDinerCountPerGroup } from 'src/components/selfService/features/LineOfCredit/hooks/useFetchDinerCountPerGroup';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import { capitalize } from 'src/utilities/string-utils';

export const getLineOfCreditAsyncTasksMessagesMap = (
  entityNameSingular: string
): Record<string, string> => ({
  [`${LineOfCreditAsyncTasks.CREATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.IN_PROGRESS}`]: `Your ${entityNameSingular} is in the works`,
  [`${LineOfCreditAsyncTasks.CREATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.SUCCESS}`]: `Your ${entityNameSingular} is ready`,
  [`${LineOfCreditAsyncTasks.CREATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.ERROR}`]: `There was a problem creating your ${entityNameSingular}. Try again.`,
  [`${LineOfCreditAsyncTasks.UPDATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.IN_PROGRESS}`]: `Updating your ${entityNameSingular}`,
  [`${LineOfCreditAsyncTasks.UPDATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.SUCCESS}`]: `Your ${entityNameSingular} has been updated`,
  [`${LineOfCreditAsyncTasks.UPDATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.ERROR}`]: `There was a problem updating your ${entityNameSingular}. Try again.`,
  [`${LineOfCreditAsyncTasks.DEACTIVATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.IN_PROGRESS}`]: `Deleting your ${entityNameSingular}`,
  [`${LineOfCreditAsyncTasks.DEACTIVATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.SUCCESS}`]: `Your ${entityNameSingular} has been deleted`,
  [`${LineOfCreditAsyncTasks.DEACTIVATE_BUDGET_BASIC_ACCOUNT}-${SnackbarStatus.ERROR}`]: `There was a problem deleting your ${entityNameSingular}. Try again.`,
});

export const LineOfCreditAnalyticsEventLabelMap = {
  [LineOfCreditAsyncTasks.CREATE_BUDGET_BASIC_ACCOUNT]: 'create new loc_cta',
  [LineOfCreditAsyncTasks.UPDATE_BUDGET_BASIC_ACCOUNT]: 'edit loc_cta',
  [LineOfCreditAsyncTasks.DEACTIVATE_BUDGET_BASIC_ACCOUNT]: 'delete loc_cta',
};

export interface ILocModalStatus {
  showModal: boolean;
  locId: string;
}
export default function LineOfCreditLanding(): JSX.Element {
  const { isPhone } = useScreenSizes();
  const {
    status: asyncTaskStatus,
    startAsyncTask,
    task_id,
    task_type,
  } = useAsyncTask();

  const {
    enterpriseData: { isInternalAdminUser },
  } = useAuth();
  const { isBalanceAccount } = useBalanceAccountInfo();
  const { value: isBalanceUiEnabled } = useFeatureFlag(
    FeatureFlag.ENABLE_BALANCE_UI
  );
  const isBalanceCorp = isBalanceAccount && isBalanceUiEnabled;

  const [searchType, setSearchType] = useState<LineOfCreditSearchType>(
    LineOfCreditSearchType.ACTIVE
  );
  const [locModalStatus, setLocModalStatus] = useState<ILocModalStatus>({
    showModal: false,
    locId: null,
  });

  const [locDeactivateModalStatus, setLocDeactivateModalStatus] =
    useState<ILocModalStatus>({
      showModal: false,
      locId: null,
    });

  const [showCreditCardErrorModal, setShowCreditCardErrorModal] =
    useState(false);
  // tracks the change between ACTIVE | INACTIVE LOCs on search so then I do not show load More when that happens
  const [isSwitching, setIsSwitching] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [accountHasCreditCard, setAccountHasCreditCard] = useState(true);
  const currentLocId = useRef(null);
  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();

  const { corpid } = useParams<{ corpid: string }>();
  const { push } = useHistory();
  const { MEAL_CREDIT_ENTITY_NAME } = useMealCreditEntityName();

  const {
    resultsList,
    hasMoreResults,
    loadMoreNumber,
    doSearch,
    searchStatus,
  } = useLineOfCreditSearch(corpid as string);

  const allGroupIdsFromLocs = Array.isArray(resultsList)
    ? getGroupIdsFromLocList(resultsList)
    : [];

  const {
    isLoading: isFetchDinerCountPerGroupLoading,
    data: dinerCountPerGroup,
  } = useFetchDinerCountPerGroup(allGroupIdsFromLocs, corpid as string);

  useEffect(() => {
    const typeLabel =
      searchType === LineOfCreditSearchType.ACTIVE ? 'Active' : 'Inactive';

    sendTealiumPageView({
      pageGroup: 'corporate self service - account management',
      pageName: `Line of Credit - ${typeLabel}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType]);

  useEffect(() => {
    if (corpid) {
      initSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType, corpid]);

  useEffect(() => {
    if (corpid) {
      verifyCreditCardExistence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid]);

  useEffect(() => {
    if (searchStatus === SearchStatus.COMPLETE) {
      setIsSwitching(false);
    }
  }, [searchStatus]);

  useEffect(() => {
    if (corpid && asyncTaskStatus === AsyncTaskStatus.COMPLETE) {
      pushSnackbarNotification(task_id, task_type, SnackbarStatus.SUCCESS);
      sendAnalytics(task_type, AnalyticsEventLabel.SUCCESS);
      if (task_type === LineOfCreditAsyncTasks.CREATE_BUDGET_BASIC_ACCOUNT) {
        goToLocDetailsPage(task_id);
      } else {
        initSearch();
      }
      closeLocModal();
      setIsLoading(false);
    }
    const initialRequestCompletedAndPollingStarted =
      corpid &&
      asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS &&
      task_type &&
      task_id;

    if (initialRequestCompletedAndPollingStarted) {
      pushSnackbarNotification(task_id, task_type, SnackbarStatus.IN_PROGRESS);
    }

    if (corpid && asyncTaskStatus === AsyncTaskStatus.ERROR) {
      pushSnackbarNotification(task_id, task_type, SnackbarStatus.ERROR);
      sendAnalytics(task_type, AnalyticsEventLabel.ERROR);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid, asyncTaskStatus, task_id, task_type]);

  function pushSnackbarNotification(
    task_id: string,
    task_type: string,
    status: SnackbarStatus
  ) {
    publish('push:snackbar', {
      id: task_id,
      status,
      message: getLineOfCreditAsyncTasksMessagesMap(
        MEAL_CREDIT_ENTITY_NAME.singular
      )[`${task_type}-${status}`],
    } as unknown as IEventData);
  }

  function sendAnalytics(
    task_type: string,
    eventlabel: AnalyticsEventLabel | string
  ) {
    sendTealiumEvent({
      eventcategory: 'self-service management tool_loc',
      eventaction: LineOfCreditAnalyticsEventLabelMap[task_type],
      eventlabel,
    });
  }

  function toggleEventType(selectedType: LineOfCreditSearchType): void {
    if (selectedType !== searchType) {
      setIsSwitching(true);
      setSearchType(selectedType);
    }
  }

  function loadMoreResults() {
    setIsSwitching(false);
    doSearch({ filter_by: searchType }, true);
  }

  async function verifyCreditCardExistence() {
    try {
      let paymentApiClientTokenUrl = `/payments/corporate/${corpid}/credit_card`;

      if (isInternalAdminUser) {
        paymentApiClientTokenUrl = `${paymentApiClientTokenUrl}/internal_admin`;
      }

      const creditCards = await sdk.http.get(paymentApiClientTokenUrl);
      if (!creditCards || !creditCards.length) {
        setAccountHasCreditCard(false);
      } else {
        setAccountHasCreditCard(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(error);
    }
  }

  async function locDeactivationConfirm(locId: string) {
    currentLocId.current = locId;
    closeLocDeactivateModal();
    try {
      await startAsyncTask(() => {
        return sdk.http.delete(`/corp-admin/${corpid}/budget`, {
          budget_ids: [locId],
        });
      });
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  async function onFormSubmit(
    budgetPayload: ILineOfCreditPayload
  ): Promise<void> {
    try {
      setIsLoading(true);
      currentLocId.current = locModalStatus.locId;
      if (locModalStatus.locId) {
        await startAsyncTask(() => {
          return sdk.http.put(
            `/corp-admin/${corpid}/budget/${locModalStatus.locId}`,
            budgetPayload
          );
        });
      } else {
        await startAsyncTask(() => {
          return sdk.http.post(`/corp-admin/${corpid}/budget`, budgetPayload);
        });
      }
    } catch (error) {
      setIsLoading(false);
      pushErrorFromAPI(error);
    }
  }

  function openLocModalForCreate(locId?: string) {
    if (!locId && !accountHasCreditCard && !isBalanceCorp) {
      setShowCreditCardErrorModal(true);
    } else {
      setLocModalStatus({
        showModal: true,
        locId,
      });
    }
  }

  function goToLocDetailsPage(locId?: string) {
    push(`/corporate/management/${corpid}/lineOfCredit/${locId}`);
  }

  function goToAccountPage() {
    push(`/corporate/management/${corpid}/account`);
  }

  function handleLocDeactivation(locId: string) {
    setLocDeactivateModalStatus({ showModal: true, locId });
  }

  function closeLocModal(): void {
    setLocModalStatus({
      showModal: false,
      locId: null,
    });
  }

  function closeLocDeactivateModal(): void {
    setLocDeactivateModalStatus({
      showModal: false,
      locId: null,
    });
  }

  function initSearch() {
    doSearch({ filter_by: searchType }, false);
  }

  function closeCreditCardErrorModal(): void {
    setShowCreditCardErrorModal(false);
  }

  function getLocDescription(loc: ILineOfCreditResponseType): string {
    const DATE_FORMAT = 'MMM d';
    if (loc.budget_reset_period === 1) {
      return `${getFormattedLocBudgetAmount(loc)} ${
        getDayLocFrequency(loc).short
      }`;
    }
    return `${getFormattedLocBudgetAmount(loc)} ${getWeekLocDates({
      loc,
      format: DATE_FORMAT,
    })} ${loc.repeating ? 'every week' : ''}`;
  }

  return (
    <Container variant="default">
      <Section inset="md">
        <Stack y={isPhone ? 'md' : 'none'}>
          <Flex
            justifyContent="space-between"
            flexDirection={isPhone ? 'column' : 'row'}
            className="lineOfCreditLanding_title"
          >
            <Stack y="md">
              <Heading variant="h1">
                {capitalize(MEAL_CREDIT_ENTITY_NAME.singular)}
              </Heading>
            </Stack>
            <Square>
              <Flex alignItems="center">
                <Button
                  className="lineOfCreditLanding_createLocButton"
                  variant="primary"
                  buttonSize="medium"
                  block
                  onClick={() => openLocModalForCreate(null)}
                >
                  {`Create a ${MEAL_CREDIT_ENTITY_NAME.singular}`}
                </Button>
              </Flex>
            </Square>
          </Flex>
        </Stack>
        <Stack y="md">
          <Row gutter={0}>
            <Column gutter={0} desktop={2} tablet={3} phone={6}>
              <Button
                className="lineOfCreditLanding_toggleButtonLeft"
                block
                buttonSize="medium"
                variant={
                  searchType === LineOfCreditSearchType.ACTIVE
                    ? 'primary'
                    : 'secondary'
                }
                onClick={() => toggleEventType(LineOfCreditSearchType.ACTIVE)}
              >
                Active
              </Button>
            </Column>
            <Column gutter={0} desktop={2} tablet={3} phone={6}>
              <Button
                className="lineOfCreditLanding_toggleButtonRight"
                block
                buttonSize="medium"
                variant={
                  searchType === LineOfCreditSearchType.INACTIVE
                    ? 'primary'
                    : 'secondary'
                }
                onClick={() => toggleEventType(LineOfCreditSearchType.INACTIVE)}
              >
                Inactive
              </Button>
            </Column>
          </Row>
        </Stack>
        {!isSwitching && (
          <>
            {searchType === LineOfCreditSearchType.ACTIVE &&
              resultsList.length > 0 && (
                <Stack y="md">
                  <UtilityBanner
                    flavor="info"
                    header="Add people to your credit"
                    message={`View your ${MEAL_CREDIT_ENTITY_NAME.singular} and add people to it so they can order`}
                  />
                </Stack>
              )}
            <Stack y="md">
              {resultsList.map((loc: ILineOfCreditResponseType) => {
                const hasDiners =
                  isFetchDinerCountPerGroupLoading || !dinerCountPerGroup
                    ? null
                    : getLocHasDiners(loc, dinerCountPerGroup);

                return (
                  <Stack y="sm" key={loc.id}>
                    <LineOfCreditCard
                      hasDiners={hasDiners}
                      key={loc.id}
                      isDisabled={
                        asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS &&
                        currentLocId.current === loc.id
                      }
                      isPhone={isPhone}
                      onEdit={(locId) => openLocModalForCreate(locId)}
                      onView={(locId) => goToLocDetailsPage(locId)}
                      onRemove={(locId) => handleLocDeactivation(locId)}
                      isActive={searchType === LineOfCreditSearchType.ACTIVE}
                      locName={loc.name}
                      locBudgetDescription={getLocDescription(loc)}
                      locId={loc.id}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </>
        )}

        {isSwitching && searchStatus !== SearchStatus.ERROR && (
          <Square inset="md" position="relative" minHeight="500px">
            <Flex
              alignItems="center"
              justifyContent="center"
              position="absolute"
              marginTop="-16px"
              marginLeft="-16px"
              width="100%"
              height="100%"
              zIndex={10}
            >
              <Loader size="lg" />
            </Flex>
          </Square>
        )}

        {resultsList.length === 0 &&
          searchStatus !== SearchStatus.ERROR &&
          searchStatus !== SearchStatus.LOADING && (
            <Square inset="md" position="relative" minHeight="500px">
              <Flex
                alignItems="center"
                justifyContent="center"
                position="absolute"
                marginTop="-16px"
                marginLeft="-16px"
                width="100%"
                height="100%"
              >
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                >
                  <Stack y="md">
                    <Image
                      src="https://res.cloudinary.com/grubhub-assets/image/upload/v1609861160/corp-management-ui/Line_of_Credit_Empty_State_Illo.svg"
                      alt="No results found"
                    />
                  </Stack>
                  {searchType === LineOfCreditSearchType.ACTIVE ? (
                    <Heading variant="h4">
                      {`Create a ${MEAL_CREDIT_ENTITY_NAME.singular} and add people to it so they can order`}
                    </Heading>
                  ) : (
                    <>
                      <Stack y="sm">
                        <Heading variant="h4">No leftovers here</Heading>
                      </Stack>
                      <Copy variant="body">
                        {`Your inactive ${MEAL_CREDIT_ENTITY_NAME.plural} will appear here`}
                      </Copy>
                    </>
                  )}
                </Flex>
              </Flex>
            </Square>
          )}

        {hasMoreResults && !isSwitching && (
          <Flex alignItems="center" justifyContent="center">
            <Button
              className="lineOfCreditLanding_loadMoreButton"
              variant="secondary"
              buttonSize="medium"
              isLoading={searchStatus === SearchStatus.LOADING}
              onClick={loadMoreResults}
            >
              Load {loadMoreNumber} more
            </Button>
          </Flex>
        )}

        {searchStatus === SearchStatus.ERROR && (
          <Square inset="sm">
            <Flex alignItems="center" justifyContent="center">
              <Copy variant="body" color="primaryText">
                Something went wrong loading your results.
              </Copy>
              <Button
                data-testid="retry-button"
                variant="tertiary"
                type="button"
                buttonSize="medium"
                onClick={initSearch}
              >
                Try again
              </Button>
            </Flex>
          </Square>
        )}
      </Section>

      {locModalStatus.showModal && (
        <LineOfCreditModal
          isLoading={isLoading}
          locId={locModalStatus.locId}
          corpId={corpid as string}
          closeModal={closeLocModal}
          onSubmit={onFormSubmit}
        />
      )}

      {locDeactivateModalStatus.showModal && (
        <SimpleDialog
          id={locDeactivateModalStatus.locId}
          primaryActionFlavor="danger"
          title={`Delete this ${MEAL_CREDIT_ENTITY_NAME.singular}?`}
          description={`After you delete this ${MEAL_CREDIT_ENTITY_NAME.singular}, people won’t be able to use it. `}
          primaryActionText="Delete"
          buttonsDirection="row-reverse"
          secondaryActionText="Cancel"
          onPrimaryActionClick={(id) => locDeactivationConfirm(id)}
          onSecondaryActionClick={closeLocDeactivateModal}
        />
      )}

      {showCreditCardErrorModal && (
        <SimpleDialog
          title={`Add a credit card to create a ${MEAL_CREDIT_ENTITY_NAME.singular}`}
          description={`Before you can create a ${MEAL_CREDIT_ENTITY_NAME.singular}, you must add a credit card to your account.`}
          primaryActionText="Add credit card"
          buttonsDirection="column"
          secondaryActionText="Cancel"
          onPrimaryActionClick={goToAccountPage}
          onSecondaryActionClick={closeCreditCardErrorModal}
        />
      )}
    </Container>
  );
}
